import {Card} from "../../../../../components/card/card.jsx";
import CardBody from "../../../../../components/card/card-body.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {lanesCameraStatusArr, lanesTypeArr} from "../../utils/lanes-utils.js";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {applyInitialSort, lanesInitialFilters} from "../../utils/lanes-list-utils.js";
import {useGeneralInput, useGeneralInputSpecialCase} from "../../../../../hooks/use-general-input.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaSearch} from "react-icons/fa";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import LanesTable from "./components/lanes-table.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import projectUtils from "../../../../../utils/project-utils.js";
import nocService from "../../../../../services/web/noc-service.js";
import {mergeLatestPassage} from "../../utils/lanes-list-utils.js";
import Loader from "../../../../../components/template/loader.jsx";
import {toast} from "react-toastify";

const API_URL = import.meta.env.VITE_API_URL;

const LanesList = ({data, maintenanceGroup, setId, setShowModal}) => {
  const [filters, setFilters] = useState(lanesInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(lanesInitialFilters);
  const [idLastPassage, setIdLastPassage] = useState(null);
  const [openLastPassage, setOpenLastPassage] = useState(false);
  const handleInput = useGeneralInput(setFilters);
  const handleSpecialInput = useGeneralInputSpecialCase(setFilters);
  const gridRef = useRef(null);
  const queryClient = useQueryClient();

  const {data: lastPassage = null, isLoading: isLoadingLastPassage, isError: isLastPassageError} = useQuery({
    queryFn: () => nocService.lastPassageImage(idLastPassage.id),
    queryKey: ["last-passage-image", idLastPassage?.id],
    enabled: openLastPassage && !!idLastPassage?.id,
  });

  useEffect(() => {
    if (!lastPassage || !idLastPassage?.id) return;

    const getLastPassageDatetime = (passage) => {
      return passage?.latestPassageDatetime || passage?.captureTime || passage?.dtLastPassage || "";
    };

    const modalLastPassageDatetime = getLastPassageDatetime(lastPassage);

    queryClient.setQueryData(["laneList"], old => {
      if (!Array.isArray(old)) return old;

      let hasChanges = false;

      const updatedList = old.map(item => {
        if (String(item.id ?? item.code) !== String(idLastPassage.id)) return item;

        if (getLastPassageDatetime(item) === modalLastPassageDatetime) return item;

        hasChanges = true;

        return mergeLatestPassage(item, lastPassage);
      });

      return hasChanges ? updatedList : old;
    });

    setIdLastPassage(current => {
      if (String(current?.id ?? current?.code) !== String(idLastPassage.id)) return current;
      if (getLastPassageDatetime(current) === modalLastPassageDatetime) return current;

      return mergeLatestPassage(current, lastPassage);
    });
  }, [idLastPassage?.id, lastPassage, queryClient]);

  const resolvedLastPassageUrl = lastPassage?.latestPassageUrl || lastPassage?.url || "";
  const resolvedLastPassageDatetime = lastPassage?.latestPassageDatetime || lastPassage?.captureTime || "";
  const lastPassageImageSrc = resolvedLastPassageUrl
    ? API_URL + "/lanes/latest-passage/get?url=" + resolvedLastPassageUrl
    : "";
  const lastPassageDate = resolvedLastPassageDatetime
    ? projectUtils.formatDate(resolvedLastPassageDatetime, "DD/MM/YYYY hh:mm:ss")
    : "";

  const parseIdFilter = useCallback((value) => {
    const rawValue = String(value ?? "").trim();
    const integerPattern = /^\d+$/;

    if (!rawValue) return {isValid: true, matches: null};

    if (rawValue.includes(",") && rawValue.includes("-")) {
      return {isValid: false, message: "Use vírgula ou intervalo no filtro de ID."};
    }

    if (rawValue.includes(",")) {
      const ids = rawValue.split(",").map(item => item.trim());

      if (ids.some(item => !integerPattern.test(item))) {
        return {isValid: false, message: "Informe apenas IDs numéricos separados por vírgula."};
      }

      const idSet = new Set(ids.map(Number));

      return {isValid: true, matches: id => idSet.has(id)};
    }

    if (rawValue.includes("-")) {
      const range = rawValue.split("-").map(item => item.trim());

      if (range.length !== 2 || range.some(item => !integerPattern.test(item))) {
        return {isValid: false, message: "Informe o intervalo de ID no formato inicial-final."};
      }

      const [start, end] = range.map(Number);

      if (start > end) {
        return {isValid: false, message: "O intervalo de ID deve começar pelo menor número."};
      }

      return {isValid: true, matches: id => id >= start && id <= end};
    }

    if (!integerPattern.test(rawValue)) {
      return {isValid: false, message: "Informe um ID numérico válido."};
    }

    const id = Number(rawValue);

    return {isValid: true, matches: itemId => itemId === id};
  }, []);

  const filteredData = useMemo(() => {
    const normalize = value => String(value ?? "").toLowerCase();
    const contains = (value, search) => normalize(value).includes(normalize(search));
    const idFilter = parseIdFilter(appliedFilters.id);

    return (data ?? []).filter(item => {
      if (appliedFilters.status) {
        const itemStatus = item.status === "alert" ? "alert" : String(!!item.status);

        if (itemStatus !== appliedFilters.status) return false;
      }

      if (idFilter.matches && !idFilter.matches(Number(item.id))) return false;
      if (appliedFilters.capturePlace && !contains(item.capturePlace, appliedFilters.capturePlace)) return false;
      if (appliedFilters.route && !contains(item.route, appliedFilters.route)) return false;
      if (appliedFilters.laneDescription && !contains(item.laneDescription, appliedFilters.laneDescription)) return false;
      if (appliedFilters.laneObservation && !contains(item.laneObservation, appliedFilters.laneObservation)) return false;

      if (appliedFilters.maintenanceGroup?.length) {
        const selectedGroups = maintenanceGroup
          .filter(group => appliedFilters.maintenanceGroup.includes(group.value))
          .map(group => group.label);

        if (!selectedGroups.some(group => contains(item.maintenanceGroup, group))) return false;
      }

      if (appliedFilters.type && item.type !== appliedFilters.type) return false;

      return true;
    });
  }, [appliedFilters, data, maintenanceGroup, parseIdFilter]);

  const clear = () => {
    if(!gridRef.current) return;
    applyInitialSort(gridRef.current);
    setFilters(lanesInitialFilters);
    setAppliedFilters(lanesInitialFilters);
    gridRef.current.setFilterModel({});
    gridRef.current.onFilterChanged();
  };
  const filter = () => {
    if(!gridRef.current) return;

    const idFilter = parseIdFilter(filters.id);

    if (!idFilter.isValid) {
      toast.error(idFilter.message);
      return;
    }

    setAppliedFilters({
      ...filters,
      maintenanceGroup: filters.maintenanceGroup ?? [],
    });
    gridRef.current.setFilterModel({});
    gridRef.current.onFilterChanged();
  }

  useGlobalKey("Escape", clear);
  useGlobalKey("Enter", filter);

  return(
    <div className="flex flex-col gap-2">
      <Card>
        <CardBody>
          <SentryRow>
            <SelectComponent
              instanceId="status"
              id="status"
              label="Status"
              value={filters.status}
              options={lanesCameraStatusArr}
              onChange={selected => handleSpecialInput("status", selected?.value)}
              classNameWrapper="col-span-2"
            />
            <InputForm
              id="id"
              label="ID da câmera"
              value={filters.id}
              onChange={handleInput}
              classNameWrapper="col-span-2"
            />
            <InputForm
              id="capturePlace"
              label="Ponto de coleta"
              value={filters.capturePlace}
              onChange={handleInput}
              classNameWrapper="col-span-4"
            />
            <InputForm
              id="route"
              label="Sentido"
              value={filters.route}
              onChange={handleInput}
              classNameWrapper="col-span-4"
            />
            <InputForm
              id="laneDescription"
              label="Nome da câmera"
              value={filters.laneDescription}
              onChange={handleInput}
              classNameWrapper="col-span-2"
            />
            <InputForm
              id="laneObservation"
              label="Observação"
              value={filters.laneObservation}
              onChange={handleInput}
              classNameWrapper="col-span-2"
            />
            <div className="col-span-8 flex gap-4">
              <SelectComponent
                instanceId="maintenanceGroup"
                id="maintenanceGroup"
                label="Responsável pela manutenção"
                value={maintenanceGroup.filter(group => filters.maintenanceGroup.includes(group.value))}
                options={maintenanceGroup}
                isMulti
                onChange={selected => handleSpecialInput("maintenanceGroup", selected?.map(item => item?.value))}
                classNameWrapper="min-w-[250px]"
              />
              <SelectComponent
                instanceId="type"
                id="type"
                label="Tipo de dispositivo"
                value={filters.type}
                options={lanesTypeArr}
                onChange={selected => handleSpecialInput("type", selected?.value)}
                classNameWrapper="flex-1"
              />
              <div className="flex gap-2">
                <Button target="outline-primary" onClick={clear} title="Limpar"><FaEraser/></Button>
                <Button onClick={filter} title="Buscar"><FaSearch/></Button>
              </div>
            </div>
          </SentryRow>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <div className="h-[calc(100vh-197px)]">
            <LanesTable
              gridRef={gridRef}
              data={filteredData}
              setId={setId}
              setShowModal={setShowModal}
              setIdLastPassage={setIdLastPassage}
              setOpenLastPassage={setOpenLastPassage}
            />
          </div>
          <Modal open={openLastPassage} setOpen={setOpenLastPassage} title={`Última passagem`} className={"w-6/12"}>
            <Modal.Body className="p-2 pt-0 flex justify-center items-center min-h-[300px]">

              {isLoadingLastPassage && <Loader/>}

              {!isLoadingLastPassage && (isLastPassageError || !lastPassageImageSrc) && (
                <img
                  src={getAssetPath("img/sentry.imgnotfound.large.748x480.png")}
                  alt="Última passagem"
                  className="max-w-full max-h-full object-contain"
                />
              )}

              {!isLoadingLastPassage && !isLastPassageError && lastPassageImageSrc && (
                <img
                  src={lastPassageImageSrc}
                  alt="Última passagem"
                  onError={(e) => e.target.src = getAssetPath("img/sentry.imgnotfound.large.748x480.png")}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </Modal.Body>
            <Modal.Footer className="flex justify-start items-center">
              <span className="text-sm text-gray-700 flex-1">
                Última passagem: <span className="font-bold">{lastPassageDate || "Não disponível"}</span>
              </span>
              <Button onClick={() => setOpenLastPassage(false)}>
                Ok
              </Button>
            </Modal.Footer>
          </Modal>
        </CardBody>
      </Card>
    </div>
  )
}

export default LanesList;